import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import ModalDetails from "../../../components/modalDetails";
import { checkDonationStatus, listData } from "../services/api";

const STATUS_STYLES = {
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  created: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
  canceled: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  refunded: "bg-violet-100 text-violet-800 border-violet-200",
};

const PAYMENT_STYLES = {
  pix: "bg-cyan-50 text-cyan-800 border-cyan-200",
  pix_automatic: "bg-indigo-50 text-indigo-800 border-indigo-200",
  paghiper: "bg-slate-50 text-slate-700 border-slate-200",
  boleto: "bg-orange-50 text-orange-800 border-orange-200",
  card: "bg-blue-50 text-blue-800 border-blue-200",
  credit_card: "bg-blue-50 text-blue-800 border-blue-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const key = value.toLowerCase();
  const style =
    STATUS_STYLES[key] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${style}`}
    >
      {value}
    </span>
  );
}

function PaymentBadge({ value }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const key = value.toLowerCase();
  const style =
    PAYMENT_STYLES[key] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${style}`}
    >
      {value}
    </span>
  );
}

export default function DonationRow({ index, donation, onFilterByRegister }) {
  const [item, setItem] = useState(donation);
  const [showModal, setShowModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  function getLookup(value) {
    if (value?.id) return { term: "id", value: value.id };
    if (value?.donationId) return { term: "id", value: value.donationId };
    if (value?.transaction_id) {
      return { term: "transaction_id", value: value.transaction_id };
    }
    return null;
  }

  function isCompleted(value) {
    return (value?.donation_status || "").toLowerCase() === "completed";
  }

  async function fetchDonationUpdate(value) {
    const lookup = getLookup(value);
    if (!lookup) return;

    const response = await listData({
      table: "donations",
      term: lookup.term,
      value: lookup.value,
      limit: 1,
      cursor: "",
    });

    const data = response?.data;
    if (!Array.isArray(data)) return;

    const updated = data.find(
      (entry) => entry && !entry.cursor && !entry.count
    );
    if (updated) setItem(updated);
  }

  async function handleCheckUpdates() {
    if (!getLookup(item)) return;
    setIsChecking(true);
    try {
      const result = await checkDonationStatus({
        transactionId: item.transaction_id,
        orderId: item.id,
        donationId: item.id,
      });
      if (result instanceof Error) {
        throw result;
      }
      await fetchDonationUpdate(item);
    } catch (error) {
      console.error("Error checking donation updates:", error);
    } finally {
      setIsChecking(false);
    }
  }

  // Country and currency live inside the donation's `info` JSON (written by
  // the /donacion page). Donations without them predate the Spanish page and
  // all came from the PT flow → Brasil / BRL.
  const donationInfo = (() => {
    try {
      const info = JSON.parse(item.info || "{}");
      return info && typeof info === "object" ? info : {};
    } catch {
      return {};
    }
  })();

  const country = donationInfo.countryName || donationInfo.country || "Brasil";
  const currency = (donationInfo.currency || "BRL").toUpperCase();

  const formattedAmount = (() => {
    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      }).format(Number(item.amount));
    } catch {
      return `${currency} ${item.amount}`;
    }
  })();

  const truncatedTx = item.transaction_id
    ? `${item.transaction_id.slice(0, 8)}…`
    : "—";

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
          {index + 1}
        </td>
        <td className="px-4 py-3 text-sm text-slate-900">
          {item.register_id && onFilterByRegister ? (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-0 p-0 text-left"
              title="Ver todas as doações deste doador"
              onClick={() => onFilterByRegister(item)}
            >
              {item.email}
            </button>
          ) : (
            item.email
          )}
        </td>
        <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
          {country}
        </td>
        <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums">
          {formattedAmount}
        </td>
        <td className="px-4 py-3 text-sm">
          <PaymentBadge value={item.payment_method} />
        </td>
        <td className="px-4 py-3 text-sm">
          <StatusBadge value={item.donation_status} />
        </td>
        <td className="px-4 py-3 text-sm text-slate-600">
          {item.donation_type}
        </td>
        <td
          className="px-4 py-3 text-sm text-slate-500 font-mono"
          title={item.transaction_id}
        >
          {truncatedTx}
        </td>
        <td className="px-4 py-3 text-sm">
          <Button
            size="sm"
            disabled={!item.info}
            variant="outline-secondary"
            onClick={() => setShowModal(true)}
          >
            Details
          </Button>
        </td>
        <td className="px-4 py-3 text-sm">
          <Button
            size="sm"
            disabled={!getLookup(item) || isChecking || isCompleted(item)}
            variant="outline-primary"
            onClick={handleCheckUpdates}
          >
            {isChecking ? <Spinner animation="border" size="sm" /> : "Check"}
          </Button>
        </td>
        <td className="px-4 py-3 text-sm font-semibold text-slate-700">
          {item.motivation_code_id}
        </td>
        <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">
          {format(new Date(item.updated_at), "dd/MM/yyyy HH:mm")}
        </td>
      </tr>
      {showModal && (
        <ModalDetails
          show={showModal}
          onHide={() => setShowModal(false)}
          args={{
            modalTitle: "Donation Details",
            buttonName: "Details",
            content: [item.info],
          }}
        />
      )}
    </>
  );
}
